import {useCallback, useEffect, useMemo, useState} from "react";
import {useLocation, useParams} from "react-router";

import {isEmpty} from "@pg-mono/nodash";
import {parseSearch} from "@pg-mono/request";

import {useLazyGetSelectedRegionBySlugQuery} from "../../region/api/get_selected_region_by_slug";
import {useLazyGetSearchByFriendlySlugQuery} from "../api/get_search_by_friendly_slug";
import {fromParsedObjectToQuery, ISlugParsed, ISlugQuery, offerUrlParser} from "../helpers/friendly_offer_list/offer_url_parser";
import {getCountryId} from "../list/helpers/sub_type/get_fetch_offer_list_subfilter_query";
import {OfferListSubType} from "../list/types/OfferListSubType";
import {IOfferListingParams} from "../types/OfferListingParams";
import {mapSearchToSearchQuery} from "../utils/map_search_to_search_query";

export interface UseParseFriendlyUrlResult {
    query: Partial<ISlugQuery> | null;
    isLoading: boolean;
    error: unknown;
    urlParams: {
        offerListSubType?: OfferListSubType;
        friendlySlug?: string;
        offerListSubFilter?: string;
        abroadRegionSlug?: string;
    };
}

export interface UseParseFriendlyUrlParams {
    priceLowerThanAverage?: boolean;
}

export function useParseFriendlyUrl(params?: UseParseFriendlyUrlParams): UseParseFriendlyUrlResult {
    const location = useLocation();
    const {friendlySlug, offerListSubFilter, abroadRegionSlug, country: countryParam, offerListSubType} = useParams<Partial<IOfferListingParams>>();
    const routeQuery = useMemo(() => parseSearch(location.search), [location.search]);

    const [query, setQuery] = useState<Partial<ISlugQuery> | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<unknown>(null);

    const [fetchRegion] = useLazyGetSelectedRegionBySlugQuery();
    const [fetchSearch] = useLazyGetSearchByFriendlySlugQuery();

    const getSearchQueryOnError = useCallback(
        async (friendlySlugParam: string | undefined): Promise<Partial<ISlugQuery> | null> => {
            if (!friendlySlugParam) return null;

            try {
                const searchResult = await fetchSearch({friendlySlug: friendlySlugParam}, true).unwrap();
                if (!searchResult) return null;

                const parsed = mapSearchToSearchQuery(searchResult, params?.priceLowerThanAverage);
                const searchQuery = fromParsedObjectToQuery(parsed);
                return {...routeQuery, ...searchQuery};
            } catch {
                return null;
            }
        },
        [routeQuery, params?.priceLowerThanAverage]
    );

    const getRegionIdAndCountry = useCallback(async (region: string | string[], country?: number): Promise<{id: number; country: number} | null> => {
        const parsedRegion = Array.isArray(region) ? region[0] : region;

        try {
            const fetchedRegion = await fetchRegion({slug: parsedRegion, country}, true).unwrap();

            if (!fetchedRegion || fetchedRegion.id === null) {
                return null;
            }

            return {id: fetchedRegion.id, country: fetchedRegion.country ?? 0};
        } catch {
            return null;
        }
    }, []);

    const buildFriendlyUrlQuery = useCallback(async (): Promise<Partial<ISlugQuery> | null> => {
        const slugParsed: Partial<ISlugParsed> | null = friendlySlug ? offerUrlParser(friendlySlug, offerListSubFilter) : null;
        const countryId = countryParam ? getCountryId(countryParam) : undefined;

        const shouldPassCountryToSlugParsed = countryId && slugParsed;
        if (shouldPassCountryToSlugParsed) {
            slugParsed.country = countryId;
        }

        const isSlugInvalid = slugParsed === null || isEmpty(slugParsed);
        if (isSlugInvalid) {
            return await getSearchQueryOnError(friendlySlug);
        }

        const region = slugParsed.region || abroadRegionSlug;
        const regionData = region ? await getRegionIdAndCountry(region, countryId) : null;

        const isRegionResolutionFailed = slugParsed.region && !regionData?.id;
        if (isRegionResolutionFailed) {
            return await getSearchQueryOnError(friendlySlug);
        }

        const hasValidRegionData = !!regionData?.id;
        if (hasValidRegionData) {
            slugParsed.region = [regionData.id.toString()];
            slugParsed.region_name = Array.isArray(region) ? region[0] : region;
        }

        const shouldAddCountryForRedirect = regionData?.country && !countryId;
        if (shouldAddCountryForRedirect) {
            slugParsed.country = regionData.country;
        }

        // Build final query
        const slugQuery = slugParsed ? fromParsedObjectToQuery(slugParsed) : {};
        return {...routeQuery, ...slugQuery};
    }, [friendlySlug, offerListSubFilter, abroadRegionSlug, countryParam, routeQuery, getSearchQueryOnError, getRegionIdAndCountry]);

    useEffect(() => {
        setIsLoading(true);
        setError(null);

        (async () => {
            try {
                const result = await buildFriendlyUrlQuery();
                setQuery(result);
            } catch (err) {
                setError(err);
                setQuery(null);
            } finally {
                setIsLoading(false);
            }
        })();
    }, [buildFriendlyUrlQuery]);

    return {
        query,
        isLoading,
        error,
        urlParams: {
            offerListSubType: offerListSubType as OfferListSubType,
            friendlySlug,
            offerListSubFilter,
            abroadRegionSlug
        }
    };
}
